import React, { useState } from 'react';

import {
  Badge,
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faDownload, faTag } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Container from 'components/shared/Container';
import {
  FILE_EXPORTS_UNSTRUCTURED_GMV_PRODUCTS,
  GLOBAL_VENDORS_FOR_DROPDOWN,
  MARK_PRODUCTS_AS_NON_BRANDED,
  UNSTRUCTURED_GMV_PRODUCTS,
} from 'lib/networking/endpoints';
import L4CategoryDropdown from 'components/shared/L4CategoryDropdown';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationBar from 'components/shared/PaginationBar';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import { SwalToast } from 'lib/utils/toast';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const PAGE_SIZE = 100;

function ProductsWithASaleIn1MAndUnstructuredView() {
  const hasUserPermissions = useUserPermissions();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedL4Category, setSelectedL4Category] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  const offset = currentPage * PAGE_SIZE;

  const { data, loading, refetch } = useGet(UNSTRUCTURED_GMV_PRODUCTS, {
    limit: PAGE_SIZE,
    offset,
    global_vendor_id: selectedVendor?.value || null,
    l4_category_id: selectedL4Category?.value || null,
  });

  const { data: countData, loading: countLoading } = useGet(UNSTRUCTURED_GMV_PRODUCTS, {
    global_vendor_id: selectedVendor?.value,
    l4_category_id: selectedL4Category?.value,
    count_only: true,
  });

  const { postData: exportUnmappedGmvProducts } = usePost(
    FILE_EXPORTS_UNSTRUCTURED_GMV_PRODUCTS,
    () => {
      SwalToast('success', 'Export started successfully. Please check the file exports page.');
    },
  );

  const { postData: markAsNonBranded, loading: markingLoading } = usePost(
    MARK_PRODUCTS_AS_NON_BRANDED,
    () => {
      SwalToast('success', 'Products marked as non-branded successfully.');
      setSelectedProductIds([]);
      refetch();
    },
  );

  const totalCount = countData?.result?.[0]?.COUNT || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const products = data?.result || [];
  const allCurrentPageSelected =
    products.length > 0 && products.every(item => selectedProductIds.includes(item.ID));

  const handlePageChange = page => {
    setCurrentPage(page);
    setSelectedProductIds([]);
  };

  const handleExport = () => {
    exportUnmappedGmvProducts({
      global_vendor_id: selectedVendor?.value || null,
      l4_category_id: selectedL4Category?.value || null,
    });
  };

  const handleSelectAll = () => {
    if (allCurrentPageSelected) {
      setSelectedProductIds(prev => prev.filter(id => !products.some(item => item.ID === id)));
    } else {
      const newIds = products.map(item => item.ID);
      setSelectedProductIds(prev => [...new Set([...prev, ...newIds])]);
    }
  };

  const handleSelectProduct = productId => {
    setSelectedProductIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId],
    );
  };

  const handleMarkAsNonBranded = () => {
    if (selectedProductIds.length === 0) {
      SwalToast('warning', 'Please select at least one product.');
      return;
    }
    markAsNonBranded({ product_ids: selectedProductIds });
  };

  return (
    <Container className={css(styles.container)}>
      <div className="d-flex justify-content-between align-items-center">
        <h3 className="font-weight-bold">Products with a sale in 1M and unstructured</h3>
        <div className="d-flex gap-2">
          <OverlayTrigger overlay={<Tooltip>Mark selected products as non-branded</Tooltip>}>
            <Button
              variant="warning"
              onClick={handleMarkAsNonBranded}
              disabled={selectedProductIds.length === 0 || markingLoading}
              className="mr-2"
            >
              {markingLoading ? (
                <Spinner animation="border" size="sm" className="mr-2" />
              ) : (
                <FontAwesomeIcon icon={faTag} className="mr-2" />
              )}
              Mark as Non-Branded ({selectedProductIds.length})
            </Button>
          </OverlayTrigger>
          {hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
            <OverlayTrigger overlay={<Tooltip>Download CSV report</Tooltip>}>
              <Button variant="outline-primary" onClick={handleExport}>
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                Export CSV
              </Button>
            </OverlayTrigger>
          )}
        </div>
      </div>
      <Row>
        <Col md={3} className="mt-3">
          <SearchableScrollableSelect
            onChange={e => {
              setSelectedVendor(e);
              setSelectedL4Category(null);
              setCurrentPage(0);
            }}
            fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
            optionMapper={vendor => ({
              label: vendor.name,
              value: vendor.id,
            })}
            autoFocus={false}
            placeholder="Select Distributor"
          />
        </Col>
        <Col md={3} className="mt-3">
          <L4CategoryDropdown
            selectedL4Category={selectedL4Category}
            setSelectedL4Category={e => {
              setSelectedL4Category(e);
              setCurrentPage(0);
            }}
            globalVendorId={selectedVendor?.value || null}
            showLabel={false}
            placeholder="Select L4 Category"
          />
        </Col>
        <Col md={3} className="mt-4">
          {countLoading ? (
            <Spinner animation="border" role="status" size="sm" />
          ) : (
            <Badge variant="warning">TOTAL: {totalCount.toLocaleString()}</Badge>
          )}
        </Col>
      </Row>
      <div className="d-flex justify-content-end mt-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Table striped bordered hover className="mt-4">
          <thead>
            <tr>
              <th className="text-center" style={{ width: '50px' }}>
                <Form.Check
                  type="checkbox"
                  checked={allCurrentPageSelected}
                  onChange={handleSelectAll}
                  title="Select all on this page"
                />
              </th>
              <th>SKU</th>
              <th>Name</th>
              <th>GTIN</th>
              <th>Distributor</th>
              <th>Monitizable GMV</th>
              <th>L4 Category</th>
              <th>Distributor Brand Name</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            {products.map(item => (
              <tr key={item.ID}>
                <td className="text-center">
                  <Form.Check
                    type="checkbox"
                    checked={selectedProductIds.includes(item.ID)}
                    onChange={() => handleSelectProduct(item.ID)}
                  />
                </td>
                <td>{item.VENDOR_SKU}</td>
                <td>{item.NAME}</td>
                <td>{item.GTIN}</td>
                <td>{item.GLOBAL_VENDOR_NAME}</td>
                <td>{`$${Number(item.MONITIZABLE_GMV?.toFixed(2) || 0).toLocaleString(
                  'en-US',
                )}`}</td>
                <td>{item.L4_CATEGORY_NAME || '-'}</td>
                <td>{item.DISTRIBUTOR_BRAND_NAME || '-'}</td>
                <td>
                  <Button
                    variant="link"
                    onClick={() => {
                      window.open(
                        `/global-product-detail/${item.GLOBAL_PRODUCT_ID}/${item.ID}/distributor`,
                        '_blank',
                      );
                    }}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <div className="d-flex justify-content-end mt-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '150vh',
    overflowX: 'hidden',
  },
});

export default ProductsWithASaleIn1MAndUnstructuredView;
